import { useCallback, useMemo, useState, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionsFilterBar } from './TransactionsFilterBar';
import { TransactionsTable } from './TransactionsTable';
import { TransactionsTableSkeleton } from './TransactionsTableSkeleton';
import { MobileTransactionCard } from './MobileTransactionCard';
import { MobileCardSkeleton } from '../../../components/common/MobileCardSkeleton/MobileCardSkeleton';
import { TransactionDetailsModal } from './TransactionDetailsModal';
import { ListPageLayout } from '../components/ListPageLayout';
import { TransactionV2, TransactionsFilterState } from '../../../types/dashboardV2';
import { useTransactionsQuery } from '../../../hooks/useDashboardQueries';
import { useListPage } from '../../../hooks/useListPage';
import { useActiveFilterCount } from '../../../hooks/useActiveFilterCount';
import { DashboardQueryKey } from '../../../constants/dashboardQueryKeys';
import {
  fromMobileTransactionsFilters,
  toMobileTransactionsFilters,
} from './transactionsMobileFilters';

const MobileFilterSheet = lazy(() => import('./MobileTransactionsFilterSheet'));

import type { MobileTransactionsFilterState } from '../../../types/mobileFilterSheetTypes';

const INITIAL_FILTERS: TransactionsFilterState = {
  search: '',
  status: '',
  dateRange: 'Last 30 days',
  minAmount: 0,
  maxAmount: 0,
  refundState: '',
};

export const TransactionsV2 = () => {
  const { t } = useTranslation();
  const {
    page,
    rowsPerPage,
    filters,
    queryFilters,
    isFilterSheetOpen,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    refresh: handleRefresh,
    openFilterSheet,
    closeFilterSheet,
  } = useListPage<TransactionsFilterState>({
    initialFilters: INITIAL_FILTERS,
    queryKeyRoot: DashboardQueryKey.TRANSACTIONS,
    urlSync: true,
    numericFilterKeys: ['minAmount', 'maxAmount'],
  });

  const [selectedTransaction, setSelectedTransaction] = useState<TransactionV2 | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const {
    data,
    isLoading: loading,
    isFetching,
    error,
  } = useTransactionsQuery({ page: page + 1, pageSize: rowsPerPage, filters: queryFilters });

  const total = data?.total ?? 0;

  // All filters (incl. minAmount/maxAmount/refundState) are applied
  // server-side, so `data.total` matches the filtered set.
  const transactions = data?.data ?? [];

  const handleApplyMobileFilters = useCallback(
    (mobile: MobileTransactionsFilterState) =>
      handleFilterChange(fromMobileTransactionsFilters(mobile)),
    [handleFilterChange],
  );

  const mobileFiltersForSheet = useMemo(
    () => toMobileTransactionsFilters(filters),
    [filters],
  );

  const handleViewDetails = useCallback((transaction: TransactionV2) => {
    setSelectedTransaction(transaction);
    setIsDetailsModalOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setIsDetailsModalOpen(false);
    setSelectedTransaction(null);
  }, []);

  const errorMessage = error instanceof Error ? error.message : t('dashboard.transactions.errorLoading');
  const activeFilterCount = useActiveFilterCount(filters, INITIAL_FILTERS);

  return (
    <>
      <ListPageLayout
        filterBar={
          <TransactionsFilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            onRefresh={handleRefresh}
          />
        }
        showLoading={loading || isFetching}
        isLoading={loading}
        hasError={Boolean(error)}
        errorMessage={errorMessage}
        onRetry={handleRefresh}
        countText={t('dashboard.transactions.displayingOfTotal', { count: transactions.length, total })}
        itemCount={transactions.length}
        emptyState={{ icon: 'receipt_long', message: t('dashboard.transactions.noTransactions') }}
        table={
          <TransactionsTable
            transactions={transactions}
            total={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onViewDetails={handleViewDetails}
          />
        }
        tableSkeleton={<TransactionsTableSkeleton />}
        mobileTitle={t('dashboard.sidebar.transactions')}
        mobileCountText={t('dashboard.transactions.allTransactionsWithCount', { count: total })}
        search={{
          value: filters.search,
          onChange: (search) => handleFilterChange({ search }),
          placeholder: t('dashboard.transactions.searchPlaceholder'),
        }}
        mobileCards={transactions.map((transaction) => (
          <MobileTransactionCard
            key={transaction.id}
            transaction={transaction}
            onViewDetails={handleViewDetails}
          />
        ))}
        mobileSkeleton={<MobileCardSkeleton />}
        pagination={{
          total,
          page,
          rowsPerPage,
          onPageChange: handlePageChange,
          onRowsPerPageChange: handleRowsPerPageChange,
        }}
        filterButton={{ activeCount: activeFilterCount, onOpen: openFilterSheet }}
        isFilterSheetOpen={isFilterSheetOpen}
        filterSheet={
          <MobileFilterSheet
            isOpen={isFilterSheetOpen}
            onClose={closeFilterSheet}
            onApply={handleApplyMobileFilters}
            initialFilters={mobileFiltersForSheet}
          />
        }
      />

      <TransactionDetailsModal
        isOpen={isDetailsModalOpen}
        transaction={selectedTransaction}
        onClose={handleCloseDetails}
      />
    </>
  );
};

export default TransactionsV2;
